import { z } from "zod";
import { monthSchema } from "./monthClosingSchemas";
import { balancesSchema, instantSchema, localDateSchema } from "./schemas";

/*
 * Runtime schemas of the bookings on the time account (T-181, ADR-0149). They mirror `backend/openapi.json` like
 * `schemas.ts`.
 */

/** The account a booking changes. */
export const timeAccountKindSchema = z.enum(["Overtime", "Flexitime"]);

/** Payout („ausgezahlt“), time off („Freizeitausgleich“) or the cap of the carry-over under § 7 (4) AZV (F-117). */
export const timeAccountBookingKindSchema = z.enum(["Payout", "TimeOff", "CarryOverCap"]);

/** A booking on the time account; negative seconds reduce the account, a reversal adds them back. */
export const timeAccountBookingSchema = z.object({
  id: z.uuid(),
  account: timeAccountKindSchema,
  kind: timeAccountBookingKindSchema,
  month: monthSchema,
  seconds: z.number().int(),
  reason: z.string(),
  createdAt: instantSchema,
  reversesBookingId: z.uuid().nullable(),
  reversedByBookingId: z.uuid().nullable(),
});

/** A warning of a booking of time off: the month may already show the time off (S4 of the review of T-181). */
export const timeAccountBookingWarningSchema = z.enum([
  "CompensatoryRestInMonth",
  "DaysWithoutWorkInMonth",
]);

/** `POST /employees/{id}/time-account-bookings`: the booking with its warnings. */
export const timeAccountBookedSchema = z.object({
  booking: timeAccountBookingSchema,
  warnings: z.array(timeAccountBookingWarningSchema),
});

/** Sums of bookings, signed like the account. */
export const timeAccountBookingTotalsSchema = z.object({
  overtimeSeconds: z.number().int(),
  flexitimeSeconds: z.number().int(),
  payoutSeconds: z.number().int(),
  timeOffSeconds: z.number().int(),
});

/** The overtime and flexitime accounts. */
export const accountBalanceSchema = z.object({
  overtimeSeconds: z.number().int(),
  flexitimeSeconds: z.number().int(),
});

const noBookings = { overtimeSeconds: 0, flexitimeSeconds: 0, payoutSeconds: 0, timeOffSeconds: 0 };

/** The bookings in `GET /me/balances`, apart from the recorded time. */
export const balanceBookingsSchema = z.object({
  month: timeAccountBookingTotalsSchema,
  year: timeAccountBookingTotalsSchema,
  carryOver: timeAccountBookingTotalsSchema,
  items: z.array(timeAccountBookingSchema),
});

/** `GET /me/balances` with the bookings and the accounts (T-181); both default for an older server. */
export const balancesWithBookingsSchema = balancesSchema.extend({
  bookings: balanceBookingsSchema.default({
    month: noBookings,
    year: noBookings,
    carryOver: noBookings,
    items: [],
  }),
  account: accountBalanceSchema.nullable().default(null),
});

/** A hint of `MILOG-ACCOUNT` (§ 2 Abs. 2 MiLoG). */
export const minimumWageHintSchema = z.object({
  kind: z.enum(["MonthlyCapExceeded", "CompensationDue", "CompensationOverdue", "ExitWithBalance"]),
  month: monthSchema,
  seconds: z.number().int(),
  limitSeconds: z.number().int().nullable(),
  dueMonth: monthSchema,
});

/** A rule of the flexitime account of § 7 AZV the account does not keep (F-117, ADR-0237). */
export const azvFlexitimeHintKindSchema = z.enum([
  "NegativeLimitExceeded",
  "CarryOverExceeded",
  "FlexiDaysExceeded",
  "AccountIncompleteAfterDeletion",
]);

/** A hint on the flexitime account of § 7 AZV; it never blocks and never changes the account. */
export const azvFlexitimeHintSchema = z.object({
  kind: azvFlexitimeHintKindSchema,
  periodFrom: localDateSchema,
  periodTo: localDateSchema,
  date: localDateSchema,
  seconds: z.number().int(),
  flexiDays: z.number(),
});

/** The flexitime account of a federal civil servant under § 7 (4) and (5) AZV (F-117). */
export const azvFlexitimeAccountSchema = z.object({
  periodFrom: localDateSchema,
  periodTo: localDateSchema,
  openingSeconds: z.number().int(),
  carriedSeconds: z.number().int(),
  balanceSeconds: z.number().int(),
  completedBalanceSeconds: z.number().int(),
  /** First day after the days deleted under § 7 (7) AZV, `null` when nothing is deleted. */
  countsFrom: localDateSchema.nullable(),
  /** Whether the balance holds the whole previous and current period; `true` from a server before the review of F-117. */
  isComplete: z.boolean().default(true),
  carryOverLimitSeconds: z.number().int(),
  negativeLimitSeconds: z.number().int(),
  flexiYearFrom: localDateSchema,
  flexiYearTo: localDateSchema,
  flexiDays: z.number(),
  maxFlexiDays: z.number().int(),
  /** Hints of a kind this client does not know yet are left out (like unknown absence kinds, F-21). */
  hints: z.array(z.unknown()).transform((items) =>
    items.flatMap((item) => {
      const parsed = azvFlexitimeHintSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    }),
  ),
});

/** `GET /me/azv-flexitime`: the caller's account, `null` outside the AZV. */
export const azvFlexitimeSchema = z.object({
  asOf: localDateSchema,
  account: azvFlexitimeAccountSchema.nullable(),
});

/** `GET /employees/{id}/time-account`. */
export const timeAccountSchema = z.object({
  asOf: localDateSchema,
  hasTimeAccount: z.boolean(),
  recorded: accountBalanceSchema,
  booked: timeAccountBookingTotalsSchema,
  balance: accountBalanceSchema,
  bookings: z.array(timeAccountBookingSchema),
  minimumWageApplies: z.boolean(),
  minimumWageHints: z.array(minimumWageHintSchema),
  /** The flexitime account of § 7 AZV (F-117); `null` outside the AZV and from an older server. */
  azvFlexitime: azvFlexitimeAccountSchema.nullable().default(null),
});

/** The account a booking changes. */
export type TimeAccountKind = z.infer<typeof timeAccountKindSchema>;
/** Payout or time off. */
export type TimeAccountBookingKind = z.infer<typeof timeAccountBookingKindSchema>;
/** A booking on the time account. */
export type TimeAccountBooking = z.infer<typeof timeAccountBookingSchema>;
/** A warning of a booking of time off. */
export type TimeAccountBookingWarning = z.infer<typeof timeAccountBookingWarningSchema>;
/** A stored booking with its warnings. */
export type TimeAccountBooked = z.infer<typeof timeAccountBookedSchema>;
/** Sums of bookings. */
export type TimeAccountBookingTotals = z.infer<typeof timeAccountBookingTotalsSchema>;
/** Balances with bookings. */
export type BalancesWithBookings = z.infer<typeof balancesWithBookingsSchema>;
/** A hint of `MILOG-ACCOUNT`. */
export type MinimumWageHint = z.infer<typeof minimumWageHintSchema>;
/** A rule of the flexitime account of § 7 AZV. */
export type AzvFlexitimeHintKind = z.infer<typeof azvFlexitimeHintKindSchema>;
/** A hint on the flexitime account of § 7 AZV. */
export type AzvFlexitimeHint = z.infer<typeof azvFlexitimeHintSchema>;
/** The flexitime account of § 7 AZV. */
export type AzvFlexitimeAccount = z.infer<typeof azvFlexitimeAccountSchema>;
/** `GET /me/azv-flexitime`. */
export type AzvFlexitime = z.infer<typeof azvFlexitimeSchema>;
/** HR's view of a time account. */
export type TimeAccount = z.infer<typeof timeAccountSchema>;

/** Query keys of the time account. */
export const timeAccountQueryKeys = {
  /** All time accounts. */
  all: ["time-account"] as const,
  /**
   * The time account of a person.
   *
   * @param employeeId - The person.
   * @returns The key.
   */
  of: (employeeId: string) => ["time-account", employeeId] as const,
};
